'use client';

import React, { useState } from 'react';
import { signIn, getSession } from 'next-auth/react';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { loginWithGoogle } from '@/core/config/firebase';
import { invalidateClientSessionCache } from '@/core/api/auth-helper';
import { Loader2 } from 'lucide-react';
import { toast } from '@/core/utils/toast';
import { useLocalizeHref } from '@/core/i18n/navigation';
import { isStaffRole } from '@/core/auth/staffRoles';

export function SocialAuthButtons({
  mode = 'login',
  onStartLoading,
  onStopLoading
}: {
  mode?: 'login' | 'register';
  onStartLoading?: () => void;
  onStopLoading?: () => void;
}) {
  const localizeHref = useLocalizeHref();
  const config = useSiteConfig();
  const [loadingProvider, setLoadingProvider] = useState<'google' | 'facebook' | null>(null);

  if (!config?.social_login_enabled) {
    return null;
  }

  const handleGoogleLogin = async () => {
    setLoadingProvider('google');
    if (onStartLoading) onStartLoading();

    try {
      const socialData = await loginWithGoogle(config?.firebase_config);

      const result = await signIn('credentials', {
        redirect: false,
        isSocial: 'true',
        email: socialData.email,
        name: socialData.name || '',
        photoUrl: socialData.photoUrl || '',
        idToken: socialData.idToken || '',
        provider: socialData.provider || 'google',
      });

      invalidateClientSessionCache();

      if (result?.error) {
        toast.error(result.error === 'MUST_RESET_PASSWORD'
          ? 'Wymagana zmiana hasła.'
          : 'Błąd autoryzacji społecznościowej.');
      } else if (result?.ok) {
        toast.success(mode === 'register' ? 'Konto utworzone pomyślnie przez Google!' : 'Zalogowano pomyślnie przez Google!');

        const session: any = await getSession();
        const role = session?.user?.role;

        const urlParams = new URLSearchParams(window.location.search);
        const callbackUrl = urlParams.get('callbackUrl');

        if (callbackUrl && callbackUrl.startsWith('/') && !callbackUrl.startsWith('//')) {
          window.location.href = callbackUrl;
        } else if (isStaffRole(role)) {
          window.location.href = '/admin';
        } else {
          window.location.href = localizeHref('/moje-konto');
        }
      }
    } catch (err: any) {
      console.error('Social login error:', err);
      const code = err?.code || '';
      if (code.includes('popup-closed-by-user')) {
        // User closed popup
      } else {
        toast.error(err?.message || 'Nie udało się połączyć z kontem Google.');
      }
    } finally {
      setLoadingProvider(null);
      if (onStopLoading) onStopLoading();
    }
  };

  const handleFacebookClick = () => {
    toast.info('Autoryzacja przez Facebook będzie dostępna niebawem!');
  };

  const separatorText = mode === 'register' ? 'lub zarejestruj się przez' : 'lub zaloguj się przez';

  return (
    <div className="w-full space-y-3.5 my-5">
      <div className="relative flex items-center justify-center">
        <div className="w-full border-t border-slate-200" />
        <span className="bg-white px-3 text-xs font-bold uppercase tracking-wider text-slate-400 shrink-0">
          {separatorText}
        </span>
        <div className="w-full border-t border-slate-200" />
      </div>

      <div className="w-full">
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={loadingProvider !== null}
          className="w-full flex items-center justify-center gap-2.5 h-11 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-bold transition-all shadow-xs active:scale-[0.99] disabled:opacity-50 cursor-pointer"
        >
          {loadingProvider === 'google' ? (
            <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
          ) : (
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
          )}
          <span>{mode === 'register' ? 'Zarejestruj się przez Google' : 'Zaloguj się przez Google'}</span>
        </button>
      </div>
    </div>
  );
}
